import React, { useEffect, useState } from "react";
import { useAppServices } from "hook/services";
import toast from "react-hot-toast";
import PrimaryButton from "../../../components/PrimaryButton";
import "./accountDetailTabs.css";

function BusinessCustomCodeTab({ formData, handleRefresh }) {
  const AppService = useAppServices();
  const [headerCode, setHeaderCode] = useState(formData?.customCode?.headerCode || "");
  const [bodyCode, setBodyCode] = useState(formData?.customCode?.bodyCode || "");
  const [embedCode, setEmbedCode] = useState(formData?.embedCode || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHeaderCode(formData?.customCode?.headerCode || "");
    setBodyCode(formData?.customCode?.bodyCode || "");
    setEmbedCode(formData?.embedCode || "");
  }, [
    formData?.id,
    formData?.customCode?.headerCode,
    formData?.customCode?.bodyCode,
    formData?.embedCode,
  ]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData?.id) {
      toast.error("Business account not found");
      return;
    }
    setSaving(true);
    const payload = {
      _id: formData.id,
      agency_id: formData.agency_id,
      customCode: {
        headerCode,
        bodyCode,
      },
      embedCode,
    };
    const { response, error } = await AppService.accounts.update({ payload });
    if (error || response?.success === false) {
      toast.error(error?.message || response?.message || "Could not save code");
    } else {
      await handleRefresh?.();
      toast.success("Code saved");
    }
    setSaving(false);
  };

  return (
    <div className="sd-account-detail-tab">
      <div className="code-editor-container">
        <form onSubmit={handleSave} className="editor-form">
          <div className="form-content">
            <div className="code-section">
              <div className="input-group">
                <label htmlFor="biz-header-code" className="input-label">
                  Header Code
                  <span className="label-description">Add your HTML head section code</span>
                </label>
                <div className="textarea-wrapper">
                  <textarea
                    id="biz-header-code"
                    value={headerCode}
                    onChange={(e) => setHeaderCode(e.target.value)}
                    placeholder="<!-- Enter your header code here -->"
                    className="code-textarea"
                    rows={8}
                    spellCheck={false}
                  />
                  <div className="textarea-footer">
                    <span className="char-count">{headerCode.length} characters</span>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="biz-body-code" className="input-label">
                  Body Code
                  <span className="label-description">Add your HTML body section code</span>
                </label>
                <div className="textarea-wrapper">
                  <textarea
                    id="biz-body-code"
                    value={bodyCode}
                    onChange={(e) => setBodyCode(e.target.value)}
                    placeholder="<!-- Enter your body code here -->"
                    className="code-textarea"
                    rows={8}
                    spellCheck={false}
                  />
                  <div className="textarea-footer">
                    <span className="char-count">{bodyCode.length} characters</span>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="biz-embed-code" className="input-label">
                  Embedded Code
                  <span className="label-description">
                    Paste the HTML, script, or iframe snippet to embed for this business listing
                  </span>
                </label>
                <div className="textarea-wrapper">
                  <textarea
                    id="biz-embed-code"
                    value={embedCode}
                    onChange={(e) => setEmbedCode(e.target.value)}
                    placeholder="<!-- Enter your embedded code here -->"
                    className="code-textarea"
                    rows={8}
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                  />
                  <div className="textarea-footer">
                    <span className="char-count">{embedCode.length} characters</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="action-section">
              <div className="save-panel">
                <div className="save-info">
                  <h3>Ready to Save?</h3>
                  <p>Header, body, and embedded code will be applied immediately after saving.</p>
                </div>

                <PrimaryButton type="submit" disabled={saving}>
                  {saving ? (
                    "Saving..."
                  ) : (
                    <>
                      <svg
                        className="save-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        aria-hidden
                      >
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17,21 17,13 7,13 7,21" />
                        <polyline points="7,3 7,8 15,8" />
                      </svg>
                      Save Code
                    </>
                  )}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </form>

        <style jsx>{`
          .code-editor-container {
            padding: 24px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          }

          .editor-form {
            background: white;
            border-radius: 12px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
            overflow: hidden;
          }

          .form-content {
            display: grid;
            grid-template-columns: 1fr 300px;
            min-height: 480px;
          }

          .code-section {
            padding: 32px;
            display: flex;
            flex-direction: column;
            gap: 24px;
          }

          .input-group {
            display: flex;
            flex-direction: column;
          }

          .input-label {
            font-size: 14px;
            font-weight: 600;
            color: #374151;
            margin-bottom: 8px;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .label-description {
            font-size: 12px;
            font-weight: 400;
            color: #3b82f6;
          }

          .textarea-wrapper {
            position: relative;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            overflow: hidden;
            transition: border-color 0.2s ease;
          }

          .textarea-wrapper:focus-within {
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .code-textarea {
            width: 100%;
            border: none;
            outline: none;
            padding: 16px;
            font-family: Monaco, Menlo, "Ubuntu Mono", monospace;
            font-size: 14px;
            line-height: 1.5;
            background: #f9fafb;
            color: #374151;
            resize: vertical;
            min-height: 120px;
          }

          .code-textarea::placeholder {
            color: #9ca3af;
          }

          .textarea-footer {
            padding: 8px 16px;
            background: #f3f4f6;
            border-top: 1px solid #e5e7eb;
            display: flex;
            justify-content: flex-end;
          }

          .char-count {
            font-size: 12px;
            color: #6b7280;
          }

          .action-section {
            background: #f8fafc;
            border-left: 1px solid #e5e7eb;
            padding: 32px 24px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .save-panel {
            text-align: center;
          }

          .save-info {
            margin-bottom: 24px;
          }

          .save-info h3 {
            font-size: 18px;
            font-weight: 600;
            color: #111827;
            margin: 0 0 8px 0;
          }

          .save-info p {
            font-size: 14px;
            color: #6b7280;
            margin: 0;
            line-height: 1.5;
          }

          .save-icon {
            width: 16px;
            height: 16px;
          }

          @media (max-width: 768px) {
            .code-editor-container {
              padding: 16px;
            }

            .form-content {
              grid-template-columns: 1fr;
            }

            .action-section {
              border-left: none;
              border-top: 1px solid #e5e7eb;
              padding: 24px;
            }

            .code-section {
              padding: 24px;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

export default BusinessCustomCodeTab;
